import React from 'react';

/**
 * FishboneDiagram - Visual SVG representation of an Ishikawa/Fishbone diagram
 * Displays cause-and-effect relationships in the classic fish-skeleton format
 */
const FishboneDiagram = ({ problemStatement, categories, rootCause }) => {
  // Configuration
  const width = 900;
  const height = 500;
  const spineY = height / 2;
  const spineStartX = 50;
  const spineEndX = width - 100;
  const headWidth = 80;

  // Category colors matching the form
  const categoryConfig = {
    man: { name: 'Man', color: '#3B82F6', position: 'top', index: 0 },
    machine: { name: 'Machine', color: '#8B5CF6', position: 'top', index: 1 },
    method: { name: 'Method', color: '#10B981', position: 'top', index: 2 },
    material: { name: 'Material', color: '#F59E0B', position: 'bottom', index: 0 },
    measurement: { name: 'Measurement', color: '#EF4444', position: 'bottom', index: 1 },
    environment: { name: 'Environment', color: '#14B8A6', position: 'bottom', index: 2 }
  };

  // Calculate branch positions
  const branchSpacing = (spineEndX - spineStartX - headWidth) / 3.5;

  const getBranchX = (index) => spineStartX + 80 + (index * branchSpacing);

  // Truncate text to fit
  const truncateText = (text, maxLength = 25) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Render a single bone (cause)
  const renderCause = (cause, index, branchX, isTop, color) => {
    const baseY = spineY + (isTop ? -40 : 40);
    const offsetY = isTop ? -(index * 25 + 15) : (index * 25 + 15);
    const causeY = baseY + offsetY;
    const causeEndX = branchX - 30 - (index * 15);

    return (
      <g key={`cause-${index}`}>
        {/* Cause line */}
        <line
          x1={branchX - 10}
          y1={baseY + (isTop ? -index * 15 : index * 15)}
          x2={causeEndX}
          y2={causeY}
          stroke={color}
          strokeWidth="1.5"
          opacity="0.7"
        />
        {/* Cause text */}
        <text
          x={causeEndX - 5}
          y={causeY}
          textAnchor="end"
          fontSize="10"
          fill="#374151"
          fontFamily="system-ui, sans-serif"
        >
          {truncateText(cause, 20)}
        </text>
      </g>
    );
  };

  // Render a category branch
  const renderBranch = (categoryKey) => {
    const config = categoryConfig[categoryKey];
    const causes = categories[categoryKey] || [];
    const validCauses = causes.filter(c => c && c.trim() !== '');
    const branchX = getBranchX(config.index);
    const isTop = config.position === 'top';
    const branchEndY = isTop ? spineY - 80 : spineY + 80;

    return (
      <g key={categoryKey}>
        {/* Main branch line */}
        <line
          x1={branchX}
          y1={spineY}
          x2={branchX - 60}
          y2={branchEndY}
          stroke={config.color}
          strokeWidth="3"
        />

        {/* Category label background */}
        <rect
          x={branchX - 110}
          y={branchEndY + (isTop ? -25 : 5)}
          width="100"
          height="20"
          rx="4"
          fill={config.color}
          opacity="0.9"
        />

        {/* Category label */}
        <text
          x={branchX - 60}
          y={branchEndY + (isTop ? -10 : 20)}
          textAnchor="middle"
          fontSize="11"
          fontWeight="bold"
          fill="white"
          fontFamily="system-ui, sans-serif"
        >
          {config.name}
        </text>

        {/* Render causes */}
        {validCauses.slice(0, 4).map((cause, idx) =>
          renderCause(cause, idx, branchX - 30, isTop, config.color)
        )}
      </g>
    );
  };

  return (
    <div className="bg-white rounded-lg p-4 overflow-x-auto">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full max-w-4xl mx-auto"
        style={{ minWidth: '700px' }}
      >
        {/* Background */}
        <rect width={width} height={height} fill="#F9FAFB" rx="8" />

        {/* Main spine (backbone) */}
        <line
          x1={spineStartX}
          y1={spineY}
          x2={spineEndX}
          y2={spineY}
          stroke="#1B5C94"
          strokeWidth="4"
        />

        {/* Arrow at the end (pointing to effect) */}
        <polygon
          points={`${spineEndX},${spineY} ${spineEndX - 15},${spineY - 10} ${spineEndX - 15},${spineY + 10}`}
          fill="#1B5C94"
        />

        {/* Fish head (effect/problem box) */}
        <rect
          x={spineEndX - 5}
          y={spineY - 45}
          width={headWidth + 20}
          height="90"
          rx="8"
          fill="#DC2626"
          opacity="0.9"
        />
        <text
          x={spineEndX + headWidth / 2 + 5}
          y={spineY - 25}
          textAnchor="middle"
          fontSize="10"
          fontWeight="bold"
          fill="white"
          fontFamily="system-ui, sans-serif"
        >
          EFFECT
        </text>
        <foreignObject
          x={spineEndX}
          y={spineY - 15}
          width={headWidth + 10}
          height="55"
        >
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            className="text-xs text-white text-center px-1 leading-tight"
            style={{
              fontSize: '9px',
              wordWrap: 'break-word',
              overflow: 'hidden',
              maxHeight: '55px'
            }}
          >
            {truncateText(problemStatement || 'Problem Statement', 50)}
          </div>
        </foreignObject>

        {/* Render all category branches */}
        {Object.keys(categoryConfig).map(key => renderBranch(key))}

        {/* Root cause box (if identified) */}
        {rootCause && rootCause.trim() !== '' && (
          <g>
            <rect
              x={spineStartX}
              y={height - 60}
              width={width - 100}
              height="45"
              rx="6"
              fill="#059669"
              opacity="0.9"
            />
            <text
              x={spineStartX + 10}
              y={height - 40}
              fontSize="10"
              fontWeight="bold"
              fill="white"
              fontFamily="system-ui, sans-serif"
            >
              ROOT CAUSE IDENTIFIED:
            </text>
            <text
              x={spineStartX + 10}
              y={height - 25}
              fontSize="11"
              fill="white"
              fontFamily="system-ui, sans-serif"
            >
              {truncateText(rootCause, 80)}
            </text>
          </g>
        )}

        {/* Title */}
        <text
          x={width / 2}
          y={25}
          textAnchor="middle"
          fontSize="14"
          fontWeight="bold"
          fill="#1B5C94"
          fontFamily="system-ui, sans-serif"
        >
          FISHBONE (ISHIKAWA) DIAGRAM
        </text>

        {/* Legend */}
        <g transform={`translate(${spineStartX}, ${height - 100})`}>
          <text fontSize="9" fill="#6B7280" fontFamily="system-ui, sans-serif">
            6M Categories: Man | Machine | Method | Material | Measurement | Environment
          </text>
        </g>
      </svg>
    </div>
  );
};

export default FishboneDiagram;
